import type { ReactElement } from 'react';
import React, { useId } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Flex } from '@rover/kibble/core';
import { isReactNative } from '@rover/rsdk/src/modules/Network/userAgent';

import SelectPill from '../SelectPills';

import type { CommonProps, onChangeType } from './MultiSelectPills.common';

export type { onChangeType };
export type Props = CommonProps;

const handleClick = (
  item: string,
  options: string[],
  values: string[],
  onChange: onChangeType
): void => {
  const newValues = options.filter((op) =>
    op === item ? !values.includes(op) : values.includes(op)
  );
  onChange(newValues);
};

const handleClickEmpty = (onChange: onChangeType): void => {
  onChange([]);
};

const MultiSelectPills = ({
  options,
  labels,
  onChange,
  values,
  hasEmptyPill = false,
  emptyPillText: emptyPillTextProp,
  disabled,
  pillStyle,
  selectedPillStyle,
  pillHoverStyle,
  pillSelectedHoverStyle,
}: Props): ReactElement => {
  const { t } = useLingui();
  const inputGroupName = useId();
  const emptyPillText = emptyPillTextProp === undefined ? t`None of the above` : emptyPillTextProp;

  return (
    <Flex flexDirection="row" flexWrap="wrap" gap={isReactNative() ? '1x' : '0x'}>
      {options.map((option, idx) => (
        <SelectPill
          onClick={(item) => handleClick(item, options, values, onChange)}
          key={option}
          isSelected={values.includes(option)}
          label={labels?.[idx] ?? option}
          value={option}
          allowSelectMultiple
          inputGroupName={inputGroupName}
          disabled={disabled}
          style={values.includes(option) ? selectedPillStyle || pillStyle : pillStyle}
          styleHover={values.includes(option) ? pillSelectedHoverStyle : pillHoverStyle}
        />
      ))}
      {hasEmptyPill ? (
        <SelectPill
          onClick={() => handleClickEmpty(onChange)}
          key={emptyPillText}
          isSelected={values.length === 0}
          label={emptyPillText}
          allowSelectMultiple
          inputGroupName={inputGroupName}
          disabled={disabled}
          style={values.length === 0 ? selectedPillStyle || pillStyle : pillStyle}
          styleHover={values.length === 0 ? pillSelectedHoverStyle : pillHoverStyle}
        />
      ) : null}
    </Flex>
  );
};

export default MultiSelectPills;
